import { lazy } from "react";

// Drop-in replacement for React.lazy that recovers from a stale-chunk
// failure (most often: the user has an old index.html in memory after a
// deploy, and the chunk URL it points at no longer exists). On failure we
// reload the page once per session — the reload pulls fresh index.html
// with current chunk hashes. The session flag prevents a reload loop if
// the chunk genuinely can't be loaded.
const RELOAD_KEY = "lazyChunkRetry";

const safeStorage = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* storage unavailable — proceed without retry guard */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export default function lazyWithRetry(factory) {
  return lazy(async () => {
    try {
      const mod = await factory();
      if (typeof window !== "undefined") safeStorage.remove(RELOAD_KEY);
      return mod;
    } catch (err) {
      if (typeof window === "undefined" || safeStorage.get(RELOAD_KEY)) {
        throw err;
      }
      safeStorage.set(RELOAD_KEY, "1");
      window.location.reload();
      // Keep Suspense in its fallback state until the reload takes over.
      return new Promise(() => {});
    }
  });
}
